import React, { createContext, useContext, useState, useEffect, useRef } from 'react';

const ThemeContext = createContext();

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

export const ThemeProvider = ({ children }) => {
  const [isDarkMode, setIsDarkMode] = useState(() => {
    // Check localStorage first, then system preference
    const savedTheme = localStorage.getItem('theme');
    if (savedTheme) {
      return savedTheme === 'dark';
    }
    // jsdom ships no matchMedia, so an unguarded call here crashes any test that
    // renders a themed page without seeding localStorage first. Light is the
    // right fallback: it is what the tokens define at :root.
    return typeof window.matchMedia === 'function'
      ? window.matchMedia('(prefers-color-scheme: dark)').matches
      : false;
  });

  const toggleTheme = () => {
    setIsDarkMode(prev => !prev);
  };

  // Idempotent setter. `toggleTheme` is the wrong tool for anything that syncs
  // the theme to a value it already knows, for example a URL param: StrictMode
  // double-invokes effects in development, so a toggle fires twice and lands
  // back where it started. Setting the value is safe however many times it runs.
  const setTheme = (mode) => {
    setIsDarkMode(mode === 'dark');
  };

  const isFirstRun = useRef(true);

  useEffect(() => {
    // Save theme preference to localStorage
    localStorage.setItem('theme', isDarkMode ? 'dark' : 'light');

    const root = document.documentElement;

    // Enable the cross-fade only while actually switching, never on first paint
    // and never permanently. The matching `.theme-switching *` rule lives in
    // styles/legacy-overrides.css.
    let timer;
    if (!isFirstRun.current) {
      root.classList.add('theme-switching');
      timer = window.setTimeout(() => root.classList.remove('theme-switching'), 320);
    }
    isFirstRun.current = false;

    // Apply theme to document root
    if (isDarkMode) {
      root.classList.add('dark-theme');
      root.classList.remove('light-theme');
    } else {
      root.classList.add('light-theme');
      root.classList.remove('dark-theme');
    }

    return () => window.clearTimeout(timer);
  }, [isDarkMode]);

  const value = {
    isDarkMode,
    toggleTheme,
    setTheme,
    theme: isDarkMode ? 'dark' : 'light'
  };

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
};
